
import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useLanguage } from "@/contexts/LanguageContext";

interface SignaturePadProps {
  onSave: (signatureData: string, type: 'draw' | 'upload') => void;
}

const SignaturePad = ({ onSave }: SignaturePadProps) => {
  const { t } = useLanguage();
  const signaturePadRef = useRef<SignatureCanvas | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(null);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      onSave('', 'draw'); // Clear the saved signature
    }
    setUploadedSignature(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Save image data as PNG for better compatibility
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.fillStyle = 'white';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              
              // Convert to PNG format
              const pngDataUrl = canvas.toDataURL('image/png');
              setUploadedSignature(pngDataUrl);
              onSave(pngDataUrl, 'upload');
              console.log("Uploaded signature processed:", pngDataUrl.substring(0, 100) + "...");
            }
          };
          img.src = reader.result;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEndDrawing = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      // Always save as PNG for better compatibility
      const signatureData = signaturePadRef.current.toDataURL('image/png');
      onSave(signatureData, 'draw');
      console.log("Drawn signature saved:", signatureData.substring(0, 100) + "...");
    }
  };

  // Ensure canvas is ready
  useEffect(() => {
    if (signaturePadRef.current) {
      signaturePadRef.current.on();
    }
    
    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
      }
    };
  }, []);

  return (
    <div className="space-y-4">
      {!uploadedSignature && (
        <div className="border rounded-lg p-4">
          <SignatureCanvas
            ref={signaturePadRef}
            onEnd={handleEndDrawing}
            canvasProps={{
              className: "signature-canvas w-full h-40 border border-border rounded",
              style: { width: '100%', height: '160px', backgroundColor: 'white' }
            }}
            backgroundColor="white"
          />
          <button 
            onClick={handleClear} 
            className="mt-2 text-sm text-muted-foreground hover:text-foreground"
            type="button"
          >
            Clear
          </button>
        </div>
      )}
      
      {uploadedSignature && (
        <div className="border rounded-lg p-4">
          <img 
            src={uploadedSignature} 
            alt="Uploaded signature" 
            className="max-h-40 mx-auto object-contain bg-background"
          />
          <button 
            onClick={handleClear} 
            className="mt-2 text-sm text-muted-foreground hover:text-foreground"
            type="button"
          >
            Clear
          </button>
        </div>
      )}

      <div className="text-center relative">
        <span className="bg-background px-2 text-sm text-muted-foreground">OR</span>
        <div className="absolute top-1/2 left-0 right-0 h-px bg-border -z-10" />
      </div>

      <div className="space-y-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
        />
        <p className="text-xs text-muted-foreground">
          Upload signature image (PNG, JPG)
        </p>
      </div>
    </div>
  );
};

export default SignaturePad;
